import type { Locale } from "@/i18n";

interface CaseStudy {
  client: string;
  line: string;
  url?: string;
}

const STUDY_EN = {
  cern: {
    client: "CERN",
    line: "Core file-transfer systems behind the 2012 Nobel-winning Higgs boson discovery: petabyte-scale data across the LHC computing grid.",
  },
  esa: {
    client: "ESA",
    line: "Deep-space telemetry and mission-planning interface with an AI backend, delivered in three weeks: it replaced the old Excel-and-email planning. Don't feel bad if you still run on Excel and email: until recently, data from behind Mars arrived the same way.",
  },
  manifestOs: {
    client: "Manifest OS",
    url: "https://manifestos.com/",
    line: "AI-native legaltech platform powering law firms: multi-tenant system on NestJS and Next.js (GCP), built with our AI-supported workflow.",
  },
  auchanGo: {
    client: "Auchan GO",
    line: "Hungary's first checkout-free shop, built under Blackbelt as prime contractor: our part was the production React Native app, covered in the national press.",
  },
  anyUpp: {
    client: "AnyUpp",
    line: "Hungary's first fully app-based restaurant ordering and payment solution, built for a global market from day one: guests ordered and paid from their phone, no waiter round-trip (Flutter, NestJS, AWS).",
  },
  uibFactor: {
    client: "UIB Factor",
    line: "Production Python pipeline processing highway collection (behajtás) cases at scale, built end to end purely with our AI-supported workflow.",
  },
  otpFizz: {
    client: "OTP Bank · Fizz.hu",
    line: "Banking-grade e-commerce on Node.js microservices and Kafka.",
  },
  erste: {
    client: "Erste Investment",
    line: "Production real-time internal trading UI, built under Blackbelt as prime contractor: the interface was our build, with our AI-supported workflow.",
  },
  gtrack: {
    client: "gTrack",
    line: "EU GINOP-funded R&D for GPS route verification on hikes and trail races: delivered and formally accepted.",
  },
} satisfies Record<string, CaseStudy>;

const STUDY_HU = {
  auchanGo: {
    client: "Auchan GO",
    line: "Magyarország első teljesen személyzet nélküli boltja, a Blackbelt fővállalkozásában: a mi részünk a boltban futó React Native app volt, országos sajtónyilvánossággal.",
  },
  anyUpp: {
    client: "AnyUpp",
    line: "Magyarország első teljesen app-alapú éttermi rendelési és fizetési megoldása, eleve globális piacra építve: a vendég a telefonjáról rendelt és fizetett, anélkül, hogy a felszolgálóra kellett volna várnia (Flutter, NestJS, AWS).",
  },
  manifestOs: {
    client: "Manifest OS",
    url: "https://manifestos.com/",
    line: "AI-natív legaltech platform ügyvédi irodáknak, amerikai bevándorlási ügyek kezelésére. A startup maga is AI-ra épül, és a fejlesztés is AI-alapú munkafolyamattal ment.",
  },
  erste: {
    client: "Erste Investment",
    line: "A Blackbelt fővállalkozásában épült valós idejű, belső kereskedési adminisztrációs felület: a felület a mi munkánk, AI-alapú munkafolyamattal.",
  },
  otpFizz: {
    client: "OTP Bank · Fizz.hu",
    line: "Banki szintű e-kereskedelem Node.js mikroszolgáltatásokon és Kafkán. A McKinsey-vel együttműködésben.",
  },
  cern: {
    client: "CERN",
    line: "A Higgs-bozon 2012-es felfedezése mögötti adatszinkronizáció: petabájtos adatok mozgatása kontinenseken keresztül az LHC Computing Grid-en.",
  },
  esa: {
    client: "ESA",
    line: "Mélyűri telemetria és küldetéstervező felület AI-backenddel a McKinsey-vel, három hét alatt: a régi Excel- és e-mail-alapú tervezést váltotta ki. Ne szégyelld, ha nálad még Excel és e-mail van: nemrég a Mars mögül is így jöttek az adatok.",
  },
  uibFactor: {
    client: "UIB Factor",
    line: "Python pipeline, amely nagy tömegben dolgozza fel az autópálya-behajtási ügyeket: elejétől a végéig, tisztán AI-alapú munkafolyamattal építve.",
  },
  gtrack: {
    client: "gTrack",
    line: "GINOP-pályázatból finanszírozott K+F: GPS-es útvonal-ellenőrzés túrákhoz és terepfutásokhoz. Leszállítva, hivatalosan elfogadva.",
  },
} satisfies Record<string, CaseStudy>;

export const SELECTED_WORK: Record<Locale, readonly CaseStudy[]> = {
  en: [
    STUDY_EN.cern,
    STUDY_EN.esa,
    STUDY_EN.manifestOs,
    STUDY_EN.auchanGo,
    STUDY_EN.uibFactor,
    STUDY_EN.otpFizz,
    STUDY_EN.erste,
    STUDY_EN.anyUpp,
    STUDY_EN.gtrack,
  ],
  hu: [
    STUDY_HU.manifestOs,
    STUDY_HU.auchanGo,
    STUDY_HU.otpFizz,
    STUDY_HU.erste,
    STUDY_HU.cern,
    STUDY_HU.esa,
    STUDY_HU.uibFactor,
    STUDY_HU.anyUpp,
    STUDY_HU.gtrack,
  ],
};

export const SELECTED_WORK_FOOTNOTE: Record<Locale, string> = {
  en: "This is only a selection. Across the twenty years there were also telecom projects in Finland, Switzerland and France, X-ray machines and other systems for General Electric and Morgan Stanley, and work for companies and startups of every size across Europe and the United States. Garlic Tech has operated since 2014: the CERN work predates that, from Zsolt Molnár's development role at the time, as part of a large international team.",
  hu: "Ez csak válogatás. Ezeken kívül a húsz év alatt készültek még telekom-projektek Finnországban, Svájcban és Franciaországban, röntgengépek és más fejlesztések a General Electricnek és a Morgan Stanley-nek, valamint munkák kisebb és nagyobb cégeknek, startupoknak Európában és az Egyesült Államokban. A Garlic Tech 2014 óta működik: a CERN-es munka ennél korábbi, Molnár Zsolt akkori fejlesztői szerepéből, egy nagy nemzetközi csapat tagjaként.",
};

export const SELECTED_WORK_LABEL: Record<Locale, string> = {
  en: "Selected Work",
  hu: "Válogatás abból, amit megépítettünk",
};

export const FAILED_STARTUPS: readonly CaseStudy[] = [
  {
    client: "Cardo Health",
    line: "Stockholm-founded telemedicine venture bringing affordable care to emerging markets: Brazil, South Africa, Colombia, Mexico.",
  },
  {
    client: "Paycap",
    line: 'Budapest fintech building fast, low-cost online and mobile payments. "Click. Send. Done."',
  },
];
